import React from 'react';
import PropTypes from 'prop-types';
import { autobind } from 'core-decorators';
import { Col, Form, Popconfirm, Row } from 'antd';
import BaseDialog from 'components/BaseDialog';
import styledComp, { FlexColContainer, FlexRowContainer } from 'components/StyledComp';
import CustomButton from 'components/CustomButton';
import Panel from 'components-desktop/Panel';
import FormInput from 'components-desktop/FormInput';
import FormTimePicker from 'components-desktop/FormTimePicker';
import helpers from 'misc/helpers';

const propTypes = {
  form: PropTypes.object.isRequired,

  evv: PropTypes.object,
  onEvvInPlaceUpdate: PropTypes.func.isRequired,
};

@autobind
class SetVisitTimeDialog extends BaseDialog {
  constructor(props) {
    super(props);

    this.state = {
      isSaveButtonLoading: false,
    };
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (this.props.evv?.getId() !== prevProps.evv?.getId()) {
      this.props.form.resetFields();
    }
  }

  async saveVisitTime(values) {
    const { evv, onEvvInPlaceUpdate } = this.props;
    const visit = evv.getRemoteObject('scheduleVisit');

    if (visit.getLong('invoiceId') > 0) {
      this.notifyError('Error', 'This visit has already been invoiced.');
      return;
    }

    if (visit.is('missed')) {
      this.notifyError('Error', 'This visit has been marked missed.');
      return;
    }

    try {
      this.setState({
        isSaveButtonLoading: true,
      });

      // Set visit in or visit out time in the visit entity.
      const fieldName = evv.getString('type') === 'SHIFT_START' ? 'visitIn' : 'visitOut';
      const params = {
        id: visit.getId(),
        [fieldName]: helpers.extraFormTimeValue(values, 'visitTime'),
      };
      await this.callService('/schedule_visit/update', params);

      // Set evv status.
      const params2 = {
        id: evv.getId(),
        status: 'SUCCESS',
      };
      await this.callService('/evv/update', params2);

      this.notifySuccess('Success', 'The visit time has been updated.');

      onEvvInPlaceUpdate(evv.getId());

      setTimeout(() => {
        this.onCancelClick();
      }, 1500);
    } catch (e) {
      this.setState({
        isSaveButtonLoading: false,
      });
    }
  }

  async onDiscardEvvClick() {
    const { evv, onEvvInPlaceUpdate } = this.props;

    try {
      this.setState({
        isSaveButtonLoading: true,
      });

      const params = {
        id: evv.getId(),
        status: 'DISCARD',
      };
      await this.callService('/evv/update', params);

      this.notifySuccess('Success', 'EVV has been discarded.');

      onEvvInPlaceUpdate(evv.getId());

      setTimeout(() => {
        this.onCancelClick();
      }, 1500);
    } catch (e) {
      this.setState({
        isSaveButtonLoading: false,
      });
    }
  }

  onCancelClick() {
    this.dismiss();

    this.setState({
      isSaveButtonLoading: false,
    });
  }

  handleSubmit(e) {
    e.preventDefault();

    this.props.form.validateFieldsAndScroll((err, values) => {
      if (!err) {
        this.saveVisitTime(values);
      }
    });
  }

  isInRenderableState() {
    return !!this.props.evv;
  }

  renderContent() {
    const { form, evv, isVisible, ...layoutProps } = this.props;
    const { isSaveButtonLoading } = this.state;
    const visit = evv.getRemoteObject('scheduleVisit');

    return (
      <styledComp.AntdModal
        {...layoutProps}
        maxWidth="900px"
        style={{ top: 100 }}
        bodyStyle={{ padding: '0 0' }}
        visible={isVisible}
        centered
        maskClosable
        footer={null}
        afterClose={this.onModalHide}
        onOk={this.onOkClick}
        onCancel={this.onCancelClick}
      >
        <Panel
          width="match_parent"
          height="wrap_content"
          color="purple_wisteria"
          title={`Set Visit Time - Visit ID: ${visit.getId()}`}
          icon="clock"
        >
          <styledComp.AntdForm
            width="match_parent"
            height="wrap_content"
            onSubmit={this.handleSubmit}
          >
            {/* Content */}
            <FlexColContainer
              width="match_parent"
              height="wrap_content"
              padding="20px 20px"
            >
              <Row type="flex" gutter={24}>
                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormInput
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="type"
                    label="EVV Type"
                    placeholder=""
                    disabled
                    initialValue={evv.getString('type') === 'SHIFT_START' ? 'Clock In' : 'Clock Out'}
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormInput
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="time"
                    label="Call Time"
                    placeholder=""
                    disabled
                    initialValue={evv.getString('timeUsString')}
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormInput
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="scheduleTime"
                    label="Schedule Time"
                    placeholder=""
                    disabled
                    initialValue={evv.getString('type') === 'SHIFT_START'
                      ? visit.getString('fromTimeString')
                      : visit.getString('toTimeString')}
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormTimePicker
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="visitTime"
                    label={evv.getString('type') === 'SHIFT_START' ? 'Visit In Time' : 'Visit Out Time'}
                    placeholder="hh:mm"
                    isRequired
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormInput
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="company"
                    label="Company Name"
                    placeholder=""
                    disabled
                    initialValue={evv.getRemoteObject('service')?.getString('companyName')}
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormInput
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="service"
                    label="Service Code"
                    placeholder=""
                    disabled
                    initialValue={evv.getRemoteObject('service')?.getString('displayCode')}
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormInput
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="patientName"
                    label="Patient Name"
                    placeholder=""
                    disabled
                    initialValue={evv.getString('patientName')}
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormInput
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="caregiverName"
                    label="Caregiver Name"
                    placeholder=""
                    disabled
                    initialValue={evv.getString('caregiverName')}
                  />
                </Col>
              </Row>
            </FlexColContainer>

            {/* Footer */}
            <FlexRowContainer
              width="match_parent"
              height="wrap_content"
              padding="20px 20px"
              justifyContent="center"
              backgroundColor="containerGrayBackground"
            >
              <CustomButton
                width="wrap_content"
                height="32px"
                margin="0 4px 0 0"
                backgroundColor="purple_wisteria"
                fontSize="14px"
                type="primary"
                loading={isSaveButtonLoading}
                htmlType="submit"
              >
                SAVE TIME AND MARK EVV SUCCESS
              </CustomButton>

              <styledComp.AntdButton
                width="wrap_content"
                height="32px"
                margin="0 4px 0 0"
                fontSize="14px"
                type="default"
                loading={isSaveButtonLoading}
                onClick={this.onCancelClick}
              >
                CANCEL
              </styledComp.AntdButton>

              <Popconfirm
                title="Are you sure you want to discard this EVV?"
                placement="top"
                okText="Confirm"
                cancelText="Cancel"
                onConfirm={this.onDiscardEvvClick}
              >
                <styledComp.AntdButton
                  width="wrap_content"
                  height="32px"
                  margin="0 0 0 32px"
                  fontSize="14px"
                  type="danger"
                  loading={isSaveButtonLoading}
                >
                  DISCARD EVV
                </styledComp.AntdButton>
              </Popconfirm>
            </FlexRowContainer>
          </styledComp.AntdForm>
        </Panel>
      </styledComp.AntdModal>
    );
  }
}

// https://facebook.github.io/react/docs/typechecking-with-proptypes.html
SetVisitTimeDialog.propTypes = propTypes;

export default Form.create()(SetVisitTimeDialog);
